import type { Gradient, Paint } from "./schema";

/** Colour helpers (pure, shared by editor, renderer and the print pipeline). */

export interface Rgba {
  r: number;
  g: number;
  b: number;
  /** 0..1 */
  a: number;
}

export function parseHex(hex: string): Rgba {
  const value = hex.replace("#", "");
  const full =
    value.length === 3
      ? value
          .split("")
          .map((c) => c + c)
          .join("")
      : value;
  const r = Number.parseInt(full.slice(0, 2), 16);
  const g = Number.parseInt(full.slice(2, 4), 16);
  const b = Number.parseInt(full.slice(4, 6), 16);
  const a = full.length >= 8 ? Number.parseInt(full.slice(6, 8), 16) / 255 : 1;
  return {
    r: Number.isFinite(r) ? r : 0,
    g: Number.isFinite(g) ? g : 0,
    b: Number.isFinite(b) ? b : 0,
    a: Number.isFinite(a) ? a : 1,
  };
}

const hex2 = (value: number) =>
  Math.round(Math.max(0, Math.min(255, value)))
    .toString(16)
    .padStart(2, "0");

export function toHex({ r, g, b, a }: Rgba): string {
  const alpha = a >= 0.999 ? "" : hex2(a * 255);
  return `#${hex2(r)}${hex2(g)}${hex2(b)}${alpha}`;
}

/** `#abc` / `#AABBCC` → `#aabbcc` (keeps alpha when present). */
export function normalizeHex(hex: string): string {
  return toHex(parseHex(hex));
}

/** Hex without alpha, lowercase. Used as key for CMYK swatches. */
export function opaqueHex(hex: string): string {
  const { r, g, b } = parseHex(hex);
  return toHex({ r, g, b, a: 1 });
}

export function hasAlpha(hex: string): boolean {
  return parseHex(hex).a < 0.999;
}

export function withAlpha(hex: string, alpha: number): string {
  return toHex({ ...parseHex(hex), a: Math.max(0, Math.min(1, alpha)) });
}

export function relativeLuminance(hex: string): number {
  const { r, g, b } = parseHex(hex);
  const channel = (value: number) => {
    const v = value / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG 2 contrast ratio between two colours (1 … 21). */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** Readable text colour (near-black or white) for a background. */
export function contrastColor(hex: string): string {
  return relativeLuminance(hex) > 0.4 ? "#111111" : "#ffffff";
}

// --- HSV (colour picker) ------------------------------------------------------

export interface Hsv {
  h: number;
  s: number;
  v: number;
}

export function rgbToHsv({ r, g, b }: Rgba): Hsv {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === rn) h = ((gn - bn) / d) % 6;
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: max === 0 ? 0 : d / max, v: max };
}

export function hsvToRgb({ h, s, v }: Hsv): { r: number; g: number; b: number } {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let rgb: [number, number, number];
  if (h < 60) rgb = [c, x, 0];
  else if (h < 120) rgb = [x, c, 0];
  else if (h < 180) rgb = [0, c, x];
  else if (h < 240) rgb = [0, x, c];
  else if (h < 300) rgb = [x, 0, c];
  else rgb = [c, 0, x];
  return { r: (rgb[0] + m) * 255, g: (rgb[1] + m) * 255, b: (rgb[2] + m) * 255 };
}

// --- CIE Lab / ΔE (gamut warnings) ---------------------------------------------

export interface Lab {
  l: number;
  a: number;
  b: number;
}

/** sRGB (D65) → CIE L*a*b* (D50, as used by ICC profiles). */
export function rgbToLab({ r, g, b }: { r: number; g: number; b: number }): Lab {
  const lin = (value: number) => {
    const v = value / 255;
    return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  const R = lin(r);
  const G = lin(g);
  const B = lin(b);
  // sRGB → XYZ (D50, Bradford-adapted)
  const X = 0.4360747 * R + 0.3850649 * G + 0.1430804 * B;
  const Y = 0.2225045 * R + 0.7168786 * G + 0.0606169 * B;
  const Z = 0.0139322 * R + 0.0971045 * G + 0.7141733 * B;
  const white = { x: 0.96422, y: 1, z: 0.82521 };
  const f = (t: number) => (t > 216 / 24389 ? Math.cbrt(t) : ((24389 / 27) * t + 16) / 116);
  const fx = f(X / white.x);
  const fy = f(Y / white.y);
  const fz = f(Z / white.z);
  return { l: 116 * fy - 16, a: 500 * (fx - fy), b: 200 * (fy - fz) };
}

/** CIEDE2000 colour difference. ~1 is barely visible, >5 clearly different. */
export function deltaE2000(lab1: Lab, lab2: Lab): number {
  const { l: L1, a: a1, b: b1 } = lab1;
  const { l: L2, a: a2, b: b2 } = lab2;
  const rad = Math.PI / 180;
  const C1 = Math.hypot(a1, b1);
  const C2 = Math.hypot(a2, b2);
  const Cm = (C1 + C2) / 2;
  const G = 0.5 * (1 - Math.sqrt(Cm ** 7 / (Cm ** 7 + 25 ** 7)));
  const a1p = (1 + G) * a1;
  const a2p = (1 + G) * a2;
  const C1p = Math.hypot(a1p, b1);
  const C2p = Math.hypot(a2p, b2);
  const h = (a: number, b: number) => {
    if (a === 0 && b === 0) return 0;
    const angle = Math.atan2(b, a) / rad;
    return angle < 0 ? angle + 360 : angle;
  };
  const h1p = h(a1p, b1);
  const h2p = h(a2p, b2);
  const dLp = L2 - L1;
  const dCp = C2p - C1p;
  let dhp = 0;
  if (C1p * C2p !== 0) {
    dhp = h2p - h1p;
    if (dhp > 180) dhp -= 360;
    else if (dhp < -180) dhp += 360;
  }
  const dHp = 2 * Math.sqrt(C1p * C2p) * Math.sin((dhp / 2) * rad);
  const Lpm = (L1 + L2) / 2;
  const Cpm = (C1p + C2p) / 2;
  let hpm = h1p + h2p;
  if (C1p * C2p !== 0) {
    if (Math.abs(h1p - h2p) > 180) hpm += h1p + h2p < 360 ? 360 : -360;
    hpm /= 2;
  }
  const T =
    1 -
    0.17 * Math.cos((hpm - 30) * rad) +
    0.24 * Math.cos(2 * hpm * rad) +
    0.32 * Math.cos((3 * hpm + 6) * rad) -
    0.2 * Math.cos((4 * hpm - 63) * rad);
  const dTheta = 30 * Math.exp(-(((hpm - 275) / 25) ** 2));
  const Rc = 2 * Math.sqrt(Cpm ** 7 / (Cpm ** 7 + 25 ** 7));
  const Sl = 1 + (0.015 * (Lpm - 50) ** 2) / Math.sqrt(20 + (Lpm - 50) ** 2);
  const Sc = 1 + 0.045 * Cpm;
  const Sh = 1 + 0.015 * Cpm * T;
  const Rt = -Math.sin(2 * dTheta * rad) * Rc;
  return Math.sqrt(
    (dLp / Sl) ** 2 + (dCp / Sc) ** 2 + (dHp / Sh) ** 2 + Rt * (dCp / Sc) * (dHp / Sh),
  );
}

export function deltaEHex(a: string, b: string): number {
  return deltaE2000(rgbToLab(parseHex(a)), rgbToLab(parseHex(b)));
}

// --- Paints ------------------------------------------------------------------

export function isGradient(paint: Paint): paint is Gradient {
  return typeof paint !== "string";
}

/** All solid colours used by a paint. */
export function paintColors(paint: Paint): string[] {
  return isGradient(paint) ? paint.stops.map((stop) => stop.color) : [paint];
}

/** CSS `background` value for a paint (same geometry as the SVG renderer). */
export function paintToCss(paint: Paint): string {
  if (!isGradient(paint)) return paint;
  const stops = [...paint.stops]
    .sort((a, b) => a.offset - b.offset)
    .map((stop) => `${stop.color} ${(stop.offset * 100).toFixed(2)}%`)
    .join(", ");
  return paint.type === "linear"
    ? `linear-gradient(${paint.angle}deg, ${stops})`
    : `radial-gradient(ellipse farthest-corner at 50% 50%, ${stops})`;
}

/** First colour of a paint, e.g. for swatches in lists. */
export function paintPreviewColor(paint: Paint): string {
  return isGradient(paint) ? (paint.stops[0]?.color ?? "#000000") : paint;
}

/** Endpoints of a CSS linear gradient line inside a w×h box. */
export function linearGradientLine(
  angle: number,
  width: number,
  height: number,
): { x1: number; y1: number; x2: number; y2: number } {
  const rad = (angle * Math.PI) / 180;
  const dx = Math.sin(rad);
  const dy = -Math.cos(rad);
  const length = Math.abs(width * dx) + Math.abs(height * dy);
  const cx = width / 2;
  const cy = height / 2;
  return {
    x1: cx - (dx * length) / 2,
    y1: cy - (dy * length) / 2,
    x2: cx + (dx * length) / 2,
    y2: cy + (dy * length) / 2,
  };
}
